import type { SandboxEntityType } from "../types/entities";
import type { InteractionType } from "../types/interactions";
import type { VectorTerm } from "../types/vectorTerm";

const SERVER_VERB: Record<string, string> = {
  follow_tapped: "followed",
  share: "shared",
  screen_captured: "saved",
  item_tapped: "viewed",
};

export function buildServerEvidenceMap(
  records: Array<{
    itemId: string;
    evidence: Array<{
      itemName: string;
      interactionType: string;
      partialScore: number;
    }>;
  }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const rec of records) {
    const top = [...(rec.evidence ?? [])].sort(
      (a, b) => b.partialScore - a.partialScore,
    )[0];
    if (top) {
      const verb = SERVER_VERB[top.interactionType] ?? "interacted with";
      map.set(rec.itemId, `Because you ${verb} ${top.itemName}`);
    }
  }
  return map;
}

const PAST_TENSE: Record<InteractionType, string> = {
  view: "viewed",
  follow: "followed",
  share: "shared",
  screen_captured: "saved",
  ticket_view: "viewed tickets for",
  ticket_purchase: "bought a ticket for",
  offer_view: "viewed an offer for",
};

/**
 * Returns a human-readable evidence string for a recommendation by finding
 * the interaction that most likely drove it. Scoring heuristic:
 *   base = decayWeight × interactionWeight
 *   ×2 if the interaction entity type matches the recommended entity type
 *   ×(1 + genreOverlapCount) if genres overlap between the interaction entity and recommendation
 */
export function computeEvidence(
  recommendationGenres: string[] | undefined,
  entityType: SandboxEntityType,
  vectorTerms: VectorTerm[],
): string | undefined {
  if (vectorTerms.length === 0) return undefined;

  let bestScore = -Infinity;
  let bestTerm: VectorTerm | null = null;

  for (const term of vectorTerms) {
    const { interaction, decayWeight, interactionWeight } = term;
    let score = decayWeight * interactionWeight;
    if (score === 0) continue;

    if (interaction.entityType === entityType) score *= 2;

    if (recommendationGenres && recommendationGenres.length > 0) {
      const interactionGenres: string[] =
        (interaction.entity as any).genres
          ?.map((g: any) => (typeof g === "string" ? g : g?.name))
          .filter(Boolean) ?? [];

      if (interactionGenres.length > 0) {
        const overlap = recommendationGenres.filter((g) =>
          interactionGenres.includes(g),
        ).length;
        score *= 1 + overlap;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestTerm = term;
    }
  }

  if (!bestTerm) return undefined;

  const { interaction } = bestTerm;
  const verb = PAST_TENSE[interaction.interactionType] ?? "interacted with";
  return `Because you ${verb} ${interaction.entity.name}`;
}
